import { useState, useEffect, useCallback } from 'react';
import MaybeLayout from '../../components/layout/MaybeLayout';
import { api } from '../../api/api';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import './healthcheck.css';

const TINT = {
  '#551FFF': 'rgba(85, 31, 255, 0.09)',
  '#22c55e': 'rgba(34, 197, 94, 0.1)',
  '#ef4444': 'rgba(239, 68, 68, 0.1)',
  '#f59e0b': 'rgba(245, 158, 11, 0.12)',
};

function HealthCheck({ user, embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/peer/monitor/overview');
      setData(res.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const scoreColor = (data?.totalDevices || 0) === 0 ? '#92959E'
    : (data?.online / data?.totalDevices) >= 0.9 ? '#22c55e'
    : (data?.online / data?.totalDevices) >= 0.7 ? '#f59e0b' : '#ef4444';

  return (
    <MaybeLayout embedded={embedded} title="System Health" user={user}>
      <div className="health-page">
        <div className="health-page__head">
          <h2>System Health Check</h2>
          <button type="button" onClick={fetchData} disabled={loading}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#551FFF', color: 'white', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
            <RefreshIcon style={{ fontSize: 16 }} /> {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        <div className="health-stat-grid">
          <StatBox icon={<DevicesOtherIcon />} label="Total Devices" value={data?.totalDevices || 0} color="#551FFF" />
          <StatBox icon={<CheckCircleIcon />} label="Online" value={data?.online || 0} color="#22c55e" />
          <StatBox icon={<WifiOffIcon />} label="Offline" value={data?.offline || 0} color="#ef4444" />
          <StatBox icon={<PlaylistRemoveIcon />} label="Empty Playlist" value={data?.withEmptyPlaylist || 0} color="#f59e0b" />
        </div>

        <div className="health-score-card">
          <MonitorHeartIcon style={{ fontSize: 44, color: scoreColor, flexShrink: 0 }} title="Health score" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 10, color: '#92959E', fontWeight: 600, textTransform: 'uppercase' }}>Health Score</p>
            <p style={{ margin: '4px 0 0', fontSize: 'clamp(1.5rem, 6vw, 2.25rem)', fontWeight: 700, color: scoreColor, lineHeight: 1.1 }}>
              {data?.totalDevices ? Math.round((data.online / data.totalDevices) * 100) : 100}%
            </p>
          </div>
          {data?.unregistered > 0 && (
            <div style={{ flexShrink: 0, background: '#fffbeb', padding: '8px 12px', borderRadius: 8, fontSize: 12, color: '#d97706', fontWeight: 600, textAlign: 'center', maxWidth: '100%' }}>
              {data.unregistered} unregistered device(s)
            </div>
          )}
        </div>

        {(data?.errors || []).length > 0 && (
          <div style={{ background: 'white', borderRadius: 14, padding: 16, boxShadow: '0 4px 16px rgba(0,0,0,0.06)', maxWidth: '100%', minWidth: 0 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700 }}>
              <ErrorIcon style={{ fontSize: 18, color: '#ef4444', verticalAlign: 'text-bottom', marginRight: 6 }} />
              Issues ({data.errors.length})
            </h3>
            <div className="health-issues-table-wrap">
              <table className="health-issues-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Device</th>
                    <th style={thStyle}>Code</th>
                    <th style={thStyle}>Store</th>
                    <th style={thStyle}>Issue</th>
                    <th style={thStyle}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {data.errors.map((e, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{e.deviceName}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>{e.deviceCode}</td>
                      <td style={tdStyle}>{e.store}</td>
                      <td style={tdStyle}>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
                          background: e.issue === 'offline' ? '#fef2f2' : '#fffbeb',
                          color: e.issue === 'offline' ? '#ef4444' : '#d97706',
                        }}>
                          {e.issue === 'offline' ? 'Offline' : 'Empty Playlist'}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontSize: 10, color: '#999' }}>{e.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {(data?.errors || []).length === 0 && !loading && (
          <div style={{ background: '#f0fdf4', borderRadius: 14, padding: 24, textAlign: 'center', color: '#16a34a', fontWeight: 600, fontSize: 14 }}>
            <CheckCircleIcon style={{ fontSize: 36, marginBottom: 8 }} /><br />
            All devices are healthy!
          </div>
        )}
      </div>
    </MaybeLayout>
  );
}

const thStyle = { textAlign: 'left', padding: '8px 8px', fontSize: 10, color: '#92959E', fontWeight: 700, textTransform: 'uppercase', borderBottom: '2px solid #f0f0f0' };
const tdStyle = { padding: '8px 8px', borderBottom: '1px solid #f5f5f7', verticalAlign: 'top' };

function StatBox({ icon, label, value, color }) {
  return (
    <div
      className="health-stat-box"
      style={{
        '--hb-color': color,
        '--hb-tint': TINT[color] || 'rgba(85, 31, 255, 0.09)',
      }}
    >
      <div className="health-stat-box__icon">{icon}</div>
      <div style={{ minWidth: 0 }}>
        <p className="health-stat-box__label">{label}</p>
        <p className="health-stat-box__value">{value}</p>
      </div>
    </div>
  );
}

export default HealthCheck;
